// lib/ai/lightweightIntentSuggestions.ts

import type { LightweightIntent } from '@/lib/task-flow/intentGuards'

export type LightweightSuggestionIntent = Exclude<LightweightIntent, 'unknown'>

export type LightweightIntentSuggestion = {
  id: string
  intent: LightweightSuggestionIntent
  label: string
  description: string
  text: string
  keywords: string[]
  aliases?: string[]
}

function normalizeSuggestionText(input: string) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[¿?¡!.,;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const LIGHTWEIGHT_INTENT_SUGGESTIONS: LightweightIntentSuggestion[] = [
  {
    id: 'create-publication-instagram',
    intent: 'open_create_publication_flow',
    label: 'Crear publicación',
    description: 'Post express para redes sociales',
    text: 'Crear una publicación para Instagram',
    keywords: [
      'crear',
      'publicacion',
      'publicación',
      'post',
      'instagram',
      'facebook',
      'tiktok',
      'redes',
      'contenido para redes',
    ],
    aliases: [
      'crear post',
      'post instagram',
      'publicacion instagram',
      'contenido redes',
      'publicar instagram',
    ],
  },
  {
    id: 'open-latest-publication-carousel',
    intent: 'open_latest_publication_carousel',
    label: 'Abrir último carrusel',
    description: 'Mostrar el último carrusel creado en Creator Studio',
    text: 'Abrir mi último carrusel creado',
    keywords: [
      'abrir',
      'abre',
      'mostrar',
      'muestra',
      'muestrame',
      'ver',
      'editar',
      'ultimo',
      'último',
      'ultima',
      'última',
      'reciente',
      'mas reciente',
      'más reciente',
      'carrusel',
      'carousel',
      'publicacion',
      'publicación',
      'post',
      'creado',
      'creada',
      'generado',
      'generada',
      'creator studio',
    ],
    aliases: [
      'abrir ultimo carrusel',
      'abrir último carrusel',
      'abre mi ultimo carrusel',
      'abre mi último carrusel',
      'muestrame mi ultimo carrusel',
      'muéstrame mi último carrusel',
      'mostrar ultimo carrusel',
      'mostrar último carrusel',
      'ver ultimo carrusel',
      'ver último carrusel',
      'editar ultimo carrusel',
      'editar último carrusel',
      'abrir carrusel reciente',
      'mostrar carrusel reciente',
      'abrir publicacion carrusel',
      'abrir publicación carrusel',
      'abrir ultima publicacion generada',
      'abrir última publicación generada',
      'mostrar ultima publicacion generada',
      'mostrar última publicación generada',
      'ver ultima publicacion creada',
      'ver última publicación creada',
    ],
  },
  {
    id: 'create-publication-facebook',
    intent: 'open_create_publication_flow',
    label: 'Crear post para Facebook',
    description: 'Publicación express para Facebook',
    text: 'Crear un post para Facebook',
    keywords: [
      'crear',
      'post',
      'facebook',
      'publicacion',
      'publicación',
      'redes',
    ],
    aliases: ['post facebook', 'publicacion facebook', 'crear facebook'],
  },
  {
    id: 'create-content-project',
    intent: 'open_create_content_flow',
    label: 'Crear contenido',
    description: 'Abrir Creator Studio',
    text: 'Crear nuevo contenido',
    keywords: [
      'crear',
      'contenido',
      'video',
      'creator studio',
      'guion',
      'hook',
      'idea',
    ],
    aliases: [
      'crear contenido',
      'nuevo contenido',
      'crear video',
      'creator studio',
    ],
  },
  {
    id: 'build-funnel',
    intent: 'open_build_funnel_flow',
    label: 'Crear embudo',
    description: 'Abrir el constructor de embudos',
    text: 'Crear un embudo',
    keywords: [
      'crear',
      'embudo',
      'funnel',
      'landing',
      'pagina',
      'página',
      'ventas',
    ],
    aliases: [
      'crear embudo',
      'construir embudo',
      'landing',
      'funnel',
      'pagina de venta',
      'página de venta',
    ],
  },
  {
    id: 'edit-product',
    intent: 'open_edit_product_flow',
    label: 'Editar producto',
    description: 'Abrir el editor de productos',
    text: 'Editar un producto',
    keywords: [
      'editar',
      'producto',
      'modificar',
      'cambiar',
      'precio',
      'reto',
      'membresia',
      'membresía',
    ],
    aliases: [
      'editar producto',
      'modificar producto',
      'cambiar producto',
      'editar reto',
      'editar membresia',
    ],
  },
  {
    id: 'create-portrait',
    intent: 'open_create_portrait_flow',
    label: 'Crear retrato',
    description: 'Generar imagen profesional',
    text: 'Crear una imagen profesional',
    keywords: [
      'crear',
      'retrato',
      'portrait',
      'imagen profesional',
      'foto profesional',
      'avatar',
      'perfil',
    ],
    aliases: [
      'crear retrato',
      'imagen profesional',
      'foto profesional',
      'crear avatar',
      'foto de perfil',
    ],
  },
  {
    id: 'create-flyer',
    intent: 'open_create_flyer_flow',
    label: 'Crear flyer',
    description: 'Diseño promocional express',
    text: 'Crear un flyer',
    keywords: [
      'crear',
      'flyer',
      'volante',
      'cartel',
      'poster',
      'póster',
      'folleto',
      'diseño',
    ],
    aliases: [
      'crear flyer',
      'hacer flyer',
      'generar flyer',
      'crear cartel',
      'crear poster',
      'crear volante',
    ],
  },
  {
    id: 'navigate-creator-studio',
    intent: 'navigate',
    label: 'Ir a Creator Studio',
    description: 'Abrir Creator Studio',
    text: 'Llévame a Creator Studio',
    keywords: [
      'ir',
      've',
      'llevame',
      'llévame',
      'abrir',
      'creator studio',
      'studio',
      'contenido',
    ],
    aliases: [
      'creator studio',
      'ir a creator studio',
      'abrir creator studio',
      'llevame a creator studio',
    ],
  },
  {
    id: 'navigate-funnels',
    intent: 'navigate',
    label: 'Ir a embudos',
    description: 'Abrir mis embudos',
    text: 'Llévame a mis embudos',
    keywords: [
      'ir',
      've',
      'llevame',
      'llévame',
      'abrir',
      'embudos',
      'funnels',
      'landing',
    ],
    aliases: [
      'mis embudos',
      'abrir embudos',
      'ir a embudos',
      'llevame a mis embudos',
    ],
  },
  {
    id: 'navigate-crm',
    intent: 'navigate',
    label: 'Ir al CRM',
    description: 'Abrir prospectos y seguimiento',
    text: 'Llévame al CRM',
    keywords: [
      'ir',
      've',
      'llevame',
      'llévame',
      'abrir',
      'crm',
      'prospectos',
      'leads',
      'seguimiento',
    ],
    aliases: [
      'crm',
      'abrir crm',
      'ir al crm',
      'prospectos',
      'leads',
    ],
  },
]

function scoreSuggestion(input: string, suggestion: LightweightIntentSuggestion) {
  const query = normalizeSuggestionText(input)

  if (!query || query.length < 2) return 0

  const text = normalizeSuggestionText(suggestion.text)
  const label = normalizeSuggestionText(suggestion.label)
  const description = normalizeSuggestionText(suggestion.description)
  const keywords = suggestion.keywords.map(normalizeSuggestionText)
  const aliases = (suggestion.aliases || []).map(normalizeSuggestionText)

  let score = 0

  if (text.startsWith(query)) score += 100
  if (label.startsWith(query)) score += 80
  if (aliases.some((alias) => alias.startsWith(query))) score += 70

  if (text.includes(query)) score += 55
  if (label.includes(query)) score += 45
  if (description.includes(query)) score += 25
  if (aliases.some((alias) => alias.includes(query))) score += 40

  const queryParts = query.split(' ').filter(Boolean)

  for (const part of queryParts) {
    if (part.length < 2) continue

    if (keywords.some((keyword) => keyword === part)) score += 20
    if (keywords.some((keyword) => keyword.includes(part))) score += 12
    if (aliases.some((alias) => alias.includes(part))) score += 10
    if (text.includes(part)) score += 8
    if (label.includes(part)) score += 6
  }

  return score
}

export function getLightweightIntentSuggestions(
  input: string,
  options?: {
    limit?: number
    allowedIntents?: LightweightSuggestionIntent[]
  }
) {
  const query = normalizeSuggestionText(input)
  const limit = options?.limit ?? 5
  const allowedIntents = options?.allowedIntents

  if (!query || query.length < 2) return []

  return LIGHTWEIGHT_INTENT_SUGGESTIONS
    .filter((suggestion) => {
      if (!allowedIntents?.length) return true
      return allowedIntents.includes(suggestion.intent)
    })
    .map((suggestion) => ({
      ...suggestion,
      score: scoreSuggestion(query, suggestion),
    }))
    .filter((suggestion) => suggestion.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
}